import React, { useMemo } from "react";
import "./ContactUsHero.css";
import { FiArrowDown } from "react-icons/fi";
import { resolveMediaUrl } from "../../../../utils/media";
import heroFallback from "../../../../assets/images/contactus/herocont.png";

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceHeroPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return coalesceHeroPayload(tryParseJsonObject(raw) || {});
  }
  if (typeof raw !== "object") return null;

  const mergeHeroWrapper = (node, into) => {
    const wrap = node?.hero ?? node?.Hero;
    if (!wrap || typeof wrap !== "object") return into;
    if (wrap.data && typeof wrap.data === "object" && !Array.isArray(wrap.data)) {
      return { ...into, ...wrap.data };
    }
    return { ...into, ...wrap };
  };

  let base = { ...raw };
  base = mergeHeroWrapper(raw, base);
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...base, ...raw.data };
    base = mergeHeroWrapper(raw.data, base);
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return mergeHeroWrapper(base, base);
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const pickHeroImage = (p) => {
  if (!p || typeof p !== "object") return "";
  const candidates = [
    p.heroImage,
    p.mainImage,
    p.image,
    p.bannerImage,
    p.mediaUrl,
    p.MediaUrl,
    p.photo,
  ];
  for (const c of candidates) {
    const url = resolveMediaUrl(c);
    if (url) return url;
  }
  return "";
};

const hasMeaningfulHeroPayload = (p) => {
  if (!p || typeof p !== "object") return false;
  if (
    pickString(
      p.tag,
      p.sectionTag,
      p.title,
      p.heading,
      p.titleLine1,
      p.titleLine2,
      p.description,
      p.body,
      p.text,
      p.summary,
      p.ctaText,
      p.buttonText,
      p.primaryButtonText
    )
  ) {
    return true;
  }
  return Boolean(pickHeroImage(p));
};

const ContactUsHero = ({ content }) => {
  const view = useMemo(() => {
    const payload = coalesceHeroPayload(content);
    if (!hasMeaningfulHeroPayload(payload) || !payload) return null;

    const titleLine1 = pickString(payload.titleLine1);
    const titleLine2 = pickString(payload.titleLine2);
    const titleSingle = pickString(payload.title, payload.heading);
    const imageSrc = pickHeroImage(payload);

    const ctaText = pickString(payload.ctaText, payload.buttonText, payload.primaryButtonText);
    const ctaLink =
      pickString(
        payload.ctaLink,
        payload.primaryButtonLink,
        payload.buttonLink,
        payload.ctaHref
      ) || (ctaText ? "#contact-form" : "");

    return {
      tag: pickString(payload.tag, payload.sectionTag),
      titleLine1,
      titleLine2,
      titleSingle,
      description: pickString(payload.description, payload.body, payload.text, payload.summary),
      ctaText,
      ctaLink,
      imageSrc,
      imageAlt: pickString(payload.heroImageAlt, payload.imageAlt, payload.altText, titleSingle, "Contact"),
    };
  }, [content]);

  if (!view) return null;

  const titleBlock =
    view.titleLine1 || view.titleLine2 ? (
      <h1>
        {view.titleLine1}
        {view.titleLine2 ? (
          <>
            {view.titleLine1 ? <br /> : null}
            <span>{view.titleLine2}</span>
          </>
        ) : null}
      </h1>
    ) : view.titleSingle ? (
      <h1>{view.titleSingle}</h1>
    ) : null;

  const showCta = Boolean(view.ctaText);

  return (
    <section className="contactUsHero">
      <div className="contactUsHero-container">
        <div className="contactUsHero-left">
          {view.tag ? <div className="contactUsHero-tag">{view.tag}</div> : null}

          {titleBlock}

          {view.description ? <p>{view.description}</p> : null}

          {showCta ? (
            <a href={view.ctaLink || "#contact-form"} className="contactUsHero-link">
              <span>{view.ctaText}</span>
              <FiArrowDown className="contactUsHero-linkIcon" />
            </a>
          ) : null}
        </div>

        {view.imageSrc ? (
          <div className="contactUsHero-right">
            <img
              src={view.imageSrc}
              alt={view.imageAlt}
              onError={(e) => {
                if (e.currentTarget.src !== heroFallback) {
                  e.currentTarget.src = heroFallback;
                }
              }}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ContactUsHero;
